import { zodResolver } from "@hookform/resolvers/zod";
import { SvcCreateUserWishlist } from "@springtree/eva-services-core";
import { HTTPError } from "ky";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";

import Button from "~/components/common/button";
import InputField from "~/components/form/input-field";
import { useWishlist } from "~/contexts/wishlist";
import { useEvaService } from "~/hooks/use-eva-service";
import { wishlistNameFields, wishlistNameFieldSchema } from "~/shared/wishlist/wishlist.model";
import { wishlistNameMaxlength, wishlistNameMinlength } from "~/shared/wishlist/zod-schemas.class";

import EVAErrors from "../form/eva-errors";
import styles from "./cart-wishlist-dialog.module.scss";

type Props = {
  productId: string;
  onSubmit: (destinationListId: string) => void;
};

export default function CartCreateWishlistForm({ productId, onSubmit }: Props) {
  const t = useTranslations();

  const { toggleWishlist, refetch, removeProdFromWishlist, savedForLaterList, list } = useWishlist();
  const createWishlist = useEvaService(SvcCreateUserWishlist);
  const [createWishlistError, setCreateWishlistError] = useState<any>();

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<wishlistNameFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(
      wishlistNameFieldSchema.refine((wl) => !list?.find((wishlist) => wishlist.Name === wl.wishlistName), {
        message: "error.Wishlist:NameMustBeUnique",
        path: ["wishlistName"],
      })
    ),
  });

  const submit = async (data: wishlistNameFields) => {
    setCreateWishlistError(undefined);

    try {
      const response = await createWishlist({
        Name: data.wishlistName,
      });
      if (response?.ID) {
        const updatedList = await refetch();
        if (updatedList.data) {
          await toggleWishlist(productId, response.ID, updatedList.data);
          if (savedForLaterList) {
            await removeProdFromWishlist(productId, savedForLaterList.ID);
          }
          onSubmit(response.ID);
        }
      }
    } catch (error) {
      if ((error as Error).name === "HTTPError") {
        const jsonError = await (error as HTTPError).response.json();
        setCreateWishlistError(jsonError);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.form}>
      <InputField
        {...register("wishlistName")}
        type="text"
        label={t("generic.myaccount.wishlist.field")}
        error={
          createWishlistError?.Error?.Type === "Wishlist:NameMustBeUnique"
            ? t(`error.${createWishlistError?.Error?.Type}`)
            : errors.wishlistName?.message
            ? t(errors.wishlistName?.message, {
                fieldName: t("generic.myaccount.wishlist.field"),
                minLength: wishlistNameMinlength,
                maxLength: wishlistNameMaxlength,
              })
            : undefined
        }
        clearValue={() => {
          setCreateWishlistError(undefined);
          setValue("wishlistName", "");
          clearErrors("wishlistName");
        }}
        className={styles.wishlistName}
      />
      {createWishlistError?.Error?.Type != "Wishlist:NameMustBeUnique" && <EVAErrors error={createWishlistError} />}
      <Button className={styles.confirmButton} variant="primary" type="submit">
        {t("generic.myaccount.wishlist.create_cta")}
      </Button>
    </form>
  );
}
